import { BasketApiService } from '@api_patient/basket_api.service';
import { Logger } from '@visiba/logger';
import { FormManager } from '@feature/dynamic_form/form_manager/form_manager.service';
import { FileManagerService } from '@feature/file_manager/mod';
import { Injectable, Scopes } from '@visiba/instantiation';
import { FormOpportunity } from '@api_patient/generated/clientv2/models/form-opportunity';
import type { MessagingBasketProductApiModel } from '@api_patient/generated/clientv2/models';

export interface Basket extends MessagingBasketProductApiModel {
	Id: number;
	ProductId: number;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class BasketService {
	public receptionId: Nullable<number> = null;
	public basketId: Nullable<number> = null;
	public basket: Nullable<Basket> = null;
	public reasonForVisitId: Nullable<number> | undefined = undefined;

	private logger = new Logger('BasketService');

	constructor(
		public readonly formManager: FormManager,
		public readonly fileManagerService: FileManagerService,
		private readonly basketApiService: BasketApiService,
	) {
		// Empty
	}
	public async delete(): AsyncResult {
		this.logger.logInformation('Clear basket');

		const deleteResult = await this.basketApiService.deleteActive(this.receptionId ?? -1);
		if (deleteResult.err) return Err(deleteResult.err);

		this.formManager.removeForms();
		this.basketId = null;
		this.basket = null;
		this.reasonForVisitId = undefined;

		return Ok();
	}

	public async restore(receptionId: number): AsyncResult<void> {
		this.logger.logInformation('Restoring basket at receptionId: {receptionId}', receptionId);

		this.receptionId = receptionId;

		if (!this.basketId) {
			this.logger.logInformation('No basket id found, creating a new basket');

			const setActiveBasketIdResult = await this.setActiveBasketId(receptionId);
			if (setActiveBasketIdResult.err) return setActiveBasketIdResult;

			return this.restore(receptionId);
		}

		this.logger.logInformation('Basket id found, fetching basket products');
		const getBasketResult = await this.basketApiService.getBasket(this.basketId);
		if (getBasketResult.err) return Err(getBasketResult.err);

		const basketProducts = getBasketResult.ok.data.MessagingProducts ?? [];

		if (basketProducts.length > 0) {
			this.populateBasket(basketProducts[0]);
		}

		return Ok();
	}

	public get(): Nullable<Basket> {
		return this.basket;
	}

	public async addData(data: {
		reasonForVisitId: number | null;
		redRobinSessionId?: string | null;
		asAgentFor?: string | null;
	}): AsyncResult {
		if (this.basketId == null) {
			return Err(new Error('BasketId is null. Run this.restore() before using this method.'));
		}

		// If basket have a product, we need to delete it before adding new data.
		// It will reset reason for visit and forms.
		if (this.basket != null) {
			const clearResponse = await this.basketApiService.deleteProduct(this.basketId, this.basket.ProductId);
			if (clearResponse.err) return Err(clearResponse.err);

			this.formManager.removeForms();
			this.reasonForVisitId = undefined;
		}

		const response = await this.basketApiService.postMessagingConversation(this.basketId, {
			ReceptionConditionId: data.reasonForVisitId,
			AsAgentFor: data.asAgentFor,
			RedRobinSessionId: data.redRobinSessionId,
		});
		if (response.err) return Err(response.err);

		this.reasonForVisitId = data.reasonForVisitId ?? null;

		return await this.restore(this.receptionId ?? -1);
	}

	private async setActiveBasketId(receptionId: number): AsyncResult {
		const response = await this.basketApiService.getActive(receptionId);
		if (response.err) {
			return Err(response.err);
		}

		this.basketId = response.ok.data;
		this.logger.logInformation('Set active basket id to {basketId}', this.basketId);

		return Ok();
	}

	private populateBasket(basket: MessagingBasketProductApiModel): Result {
		const id = this.basketId;
		if (id == null) return Err(new Error('BasketId is null. Run this.restore() before using this method.'));

		this.formManager.addRespondants(
			basket.Forms?.map((form) => ({
				completed: form.Answered,
				editable: true,
				formId: form.Id,
				header: form.Header ?? '',
				opportunity: FormOpportunity.BeforeCase,
				basketId: id,
				basketProductId: basket.Id,
				issueTrackingId: '',
				dataProvider: { type: 'basket', basketId: id, basketProductId: basket.Id },
			})) ?? [],
		);

		this.basket = {
			...basket,
			Id: id,
			ProductId: basket.Id,
		};
		this.reasonForVisitId = basket.ReceptionConditionId;

		return Ok();
	}
}
